import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '@/shared/lib/cn';

export const InputVariants = cva(
  `
  w-full
  h-9
  rounded-[0.5rem]
  bg-background
  hover:border-gradient-border

  px-[0.625rem]
  text-[0.875rem]
  text-input-text
  placeholder:text-input-text
  transition-all
  hover:shadow-[0_0_6px_var(--input-glow)]
  focus:shadow-[0_0_6px_var(--input-glow)]
  focus:outline-none
  `,
  {
    variants: {
      icon: {
        none: '',
        start: 'pl-[2rem]',
        end: 'pr-[2rem]',
        both: 'pl-[2rem] pr-[2rem]',
      },
      outline: {
        true: ' border border-primary-active-border',
        false: 'border border-outline',
      },
    },

    defaultVariants: {
      icon: 'none',
      outline: true,
    },
  },
);

export const LabelVariants = cva(`
  absolute
  left-[0.5rem]
  -top-[0.45rem]

  px-[0.25rem]

  text-[0.75rem]
  text-primary-active
  leading-none

  bg-background

  z-[2]
  pointer-events-none
  select-none
`);

export const MessageVariants = cva(
  `
  pl-2
  text-[0.75rem]
  `,
  {
    variants: {
      variant: {
        helper: 'text-primary',
        error: 'text-negative',
      },
    },

    defaultVariants: {
      variant: 'helper',
    },
  },
);

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>, VariantProps<typeof InputVariants> {
  label?: string;
  helperText?: string;
  error?: string;

  iconStart?: React.ReactNode;
  iconEnd?: React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ label, helperText, error, iconStart, iconEnd, className, ...props }, ref) => {
    const hasIconStart = Boolean(iconStart);
    const hasIconEnd = Boolean(iconEnd);

    const iconVariant =
      hasIconStart && hasIconEnd ? 'both' : hasIconStart ? 'start' : hasIconEnd ? 'end' : 'none';

    return (
      <div className="flex flex-col w-full">
        <div className="relative">
          {label && <label className={LabelVariants()}>{label}</label>}

          <div
            className="
              relative
              flex
              items-center
              rounded-[0.5rem]
            "
          >
            {iconStart && (
              <span className="absolute left-[0.625rem] flex items-center text-primary">
                {iconStart}
              </span>
            )}
            <input
              ref={ref}
              className={cn(
                InputVariants({
                  icon: iconVariant,
                  outline: props.outline,
                }),
                className,
                iconStart && 'pl-12',
                iconEnd && 'pr-12',
              )}
              {...props}
            />

            {iconEnd && (
              <span className="absolute right-[0.625rem] flex items-center text-primary">
                {iconEnd}
              </span>
            )}
          </div>
        </div>

        {(helperText || error) && (
          <span className={`${MessageVariants({ variant: error ? 'error' : 'helper' })}`}>
            {error ?? helperText}
          </span>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
